import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { OnboardingBoardRowDTO } from "../../data/onboardingApi";
import { listDrafts } from "../../data/onboardingApi";
import { ALL_CO_SUBSTEPS, CO_BASE } from "../../domain/steps";
import { coTenantDomain, SERVICE_MODULE_LABEL } from "../../domain/constants";
import { CoButton } from "../../components/Button";
import { cn } from "../../components/cn";
import "../../styles.css";

const STEP_TITLE: Record<string, string> = ALL_CO_SUBSTEPS.reduce<Record<string, string>>((all, sub) => {
  all[sub.slug] = sub.title;
  return all;
}, {});

const STATUS_LABEL: Record<string, string> = {
  draft: "Not started",
  in_progress: "In progress",
  ready_to_provision: "Ready to provision",
  live: "Live",
  abandoned: "Abandoned",
};

const STATUS_STYLE: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  in_progress: "bg-primary/10 text-primary",
  ready_to_provision: "bg-primary/10 text-primary",
  live: "bg-primary text-primary-foreground",
  abandoned: "bg-muted text-muted-foreground",
};

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  // The API returns MySQL datetimes; treat them as UTC.
  const then = new Date(iso.replace(" ", "T") + "Z").getTime();
  if (Number.isNaN(then)) return "";

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(then).toLocaleDateString();
}

/**
 * Two tabs, split on the one status change that changes what a row IS: before
 * go-live a row is work to finish, after it is a client to look in on. One
 * mixed list made the working set harder to scan as live clients accumulated.
 * Abandoned rows stay under In progress — they are onboarding that stopped,
 * not clients.
 */
type BoardTab = "in-progress" | "live";

const isLive = (row: OnboardingBoardRowDTO) => row.status === "live";

/**
 * The onboarding board: every client with the step it's sitting on, most
 * recently touched first. This is what makes "save and come back later" real —
 * a half-finished onboarding has a way back in.
 */
export function OnboardingBoardPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<OnboardingBoardRowDTO[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<BoardTab>("in-progress");

  const load = useCallback(() => {
    setError(undefined);
    listDrafts()
      .then(setRows)
      .catch((cause: Error) => setError(cause.message));
  }, []);

  useEffect(load, [load]);

  function startNew() {
    setCreating(true);
    navigate(`${CO_BASE}/new`);
  }

  const liveCount = rows === undefined ? 0 : rows.filter(isLive).length;
  const visible = rows === undefined ? [] : rows.filter((row) => isLive(row) === (tab === "live"));

  return (
    // h-screen + own scroll: the admin shell sets body{overflow:hidden}, so
    // the page must scroll itself once the client lists outgrow the viewport.
    <div className="co-root h-screen overflow-y-auto p-10 text-left">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">Client onboarding</h1>
            <p className="text-sm text-muted-foreground">
              Start a new client, or pick up one you left part-way through.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CoButton variant="outline" onClick={() => navigate("/admin/dashboard")}>
              Back to dashboard
            </CoButton>
            <CoButton onClick={startNew} disabled={creating}>
              New client
            </CoButton>
          </div>
        </div>

        {error && (
          <div className="flex flex-col items-start gap-3 rounded-lg border p-6">
            <p className="text-sm text-destructive">{error}</p>
            <CoButton variant="outline" onClick={load}>
              Try again
            </CoButton>
          </div>
        )}

        {!error && rows === undefined && (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((row) => (
              <div key={row} className="h-16 animate-pulse rounded-lg bg-muted/40" />
            ))}
          </div>
        )}

        {!error && rows !== undefined && rows.length === 0 && (
          <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-10">
            <h2 className="text-base font-medium">No clients yet</h2>
            <p className="text-sm text-muted-foreground">
              Onboarding walks you through company details, connecting the books, and going live.
            </p>
            <CoButton onClick={startNew} disabled={creating}>
              Onboard your first client
            </CoButton>
          </div>
        )}

        {!error && rows !== undefined && rows.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex w-fit gap-1 rounded-lg border bg-muted/40 p-1" role="tablist">
              {(
                [
                  { key: "in-progress", label: "In progress", count: rows.length - liveCount },
                  { key: "live", label: "Live", count: liveCount },
                ] as const
              ).map(({ key, label, count }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    tab === key ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {label} <span className="text-xs text-muted-foreground">{count}</span>
                </button>
              ))}
            </div>

            {visible.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8">
                <p className="text-sm text-muted-foreground">
                  {tab === "live"
                    ? "No live clients yet. A client moves here once its go-live step completes."
                    : "Nothing in progress. Every client here has gone live."}
                </p>
              </div>
            ) : (
              <ul className="flex flex-col divide-y rounded-lg border">
                {visible.map((row) => (
                  <li key={row.public_id}>
                    <button
                      type="button"
                      onClick={() => navigate(`${CO_BASE}/${row.public_id}/${row.current_step}`)}
                      className="flex w-full flex-wrap items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-muted/40"
                    >
                      <div className="flex min-w-0 flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">{row.name || "Untitled client"}</span>
                          <span
                            className={cn(
                              "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
                              STATUS_STYLE[row.status] ?? STATUS_STYLE.draft
                            )}
                          >
                            {STATUS_LABEL[row.status] ?? row.status}
                          </span>
                        </div>
                        <span className="truncate text-xs text-muted-foreground">
                          {row.subdomain ? `${row.subdomain}.${coTenantDomain(row.tenant_domain ?? undefined)} · ` : ""}
                          {row.services.length > 0
                            ? row.services
                                .map(
                                  (service) =>
                                    SERVICE_MODULE_LABEL[service as keyof typeof SERVICE_MODULE_LABEL] ?? service
                                )
                                .join(", ")
                            : "No services chosen yet"}
                        </span>
                      </div>

                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-sm">{STEP_TITLE[row.current_step] ?? row.current_step}</span>
                        <span className="text-xs text-muted-foreground">
                          {row.steps_done} {row.steps_done === 1 ? "step" : "steps"} done · {relativeTime(row.updated_at)}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
